import { Fragment, useMemo } from 'react';
import { StreakXpBadge } from '../ds';
import { WEB_ICO } from '../shell/icons';
import { WebRing } from '../shell/WebRing';
import { MockTestCard, PathConnector, PathNode, TestBracket, type PathNodeData } from '../screens/path/PathNode';
import { ErrorState, LoadingState } from '../components/ScreenState';
import { useAsync } from '../hooks/useAsync';
import { contentApi } from '../api/content';
import { progressApi } from '../api/progress';
import { mockTestApi } from '../api/mockTest';
import { DEFAULT_COURSE_ID } from '../constants';
import type { UnitSummary } from '../api/content.types';
import type { GamificationView, NodeProgress, ProgressView } from '../api/progress.types';
import type { MockTestSummary } from '../api/mockTest.types';

const FOUNDATION_META: Record<string, { label: string; jp: string }> = {
  Hiragana: { label: 'Unit 0', jp: 'あ' },
  Katakana: { label: 'Unit 0.5', jp: 'ア' },
  'Bộ thủ': { label: 'Unit 0.75', jp: '部' },
};

/// Unit "nền tảng" có order 0..2; unit "Bài N" có order = N + BAI_PATH_ORDER_OFFSET —
/// PHẢI khớp packages/database/prisma/seed/index.ts và apps/api/src/progress/progress.service.ts.
const BAI_PATH_ORDER_OFFSET = 2;

/// Chưa có dữ liệu tiến độ (đang tải / user mới) → coi như node mở, KHÔNG khoá.
const DEFAULT_NODE_PROGRESS: NodeProgress = { state: 'open', pct: 0 };

/// Khoảng cách giữa 3 cột nhánh — ngoặc nối thẻ Bài test tính tâm cột theo số này.
const BRANCH_GAP = 12;

function foundationNode(unit: UnitSummary, progress: ProgressView | null): PathNodeData {
  const meta = FOUNDATION_META[unit.name] ?? { label: `Unit ${unit.order}`, jp: unit.name.charAt(0) };
  const node = progress?.foundation[unit.order] ?? DEFAULT_NODE_PROGRESS;
  return { id: unit.id, label: meta.label, name: unit.name, jp: meta.jp, meta: `${unit.lessonCount} bài`, state: node.state, pct: node.pct };
}

function branchNodes(unit: UnitSummary, progress: ProgressView | null) {
  const baiNumber = unit.order - BAI_PATH_ORDER_OFFSET;
  // `branch?.exercise` optional-chained riêng (không chỉ fallback cả object) vì
  // response cũ (trước khi API thêm field này) vẫn có `vocab`/`grammar` hợp lệ.
  const branch = progress?.branch[baiNumber];
  const vocabProgress = branch?.vocab ?? DEFAULT_NODE_PROGRESS;
  const grammarProgress = branch?.grammar ?? DEFAULT_NODE_PROGRESS;
  const exerciseProgress = branch?.exercise ?? DEFAULT_NODE_PROGRESS;
  return {
    baiNumber,
    vocab: {
      id: `${unit.id}-vocab`,
      label: `V-Unit ${baiNumber}`,
      name: `Từ vựng ${baiNumber}`,
      jp: '語',
      meta: `${unit.lessonCount} bài`,
      state: vocabProgress.state,
      pct: vocabProgress.pct,
    } satisfies PathNodeData,
    grammar: {
      id: `${unit.id}-grammar`,
      label: `G-Unit ${baiNumber}`,
      name: `Ngữ pháp ${baiNumber}`,
      jp: '文',
      meta: `${unit.lessonCount} bài`,
      state: grammarProgress.state,
      pct: grammarProgress.pct,
    } satisfies PathNodeData,
    exercise: {
      id: `${unit.id}-exercise`,
      label: `E-Unit ${baiNumber}`,
      name: `Bài tập ${baiNumber}`,
      jp: '練',
      meta: '12 câu · 3 phần',
      state: exerciseProgress.state,
      pct: exerciseProgress.pct,
    } satisfies PathNodeData,
  };
}

function TrackHeadPill({ icon, label }: { icon: 'heart' | 'book' | 'edit'; label: string }) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        background: 'var(--surface-tint)',
        border: '1px solid var(--border-default)',
        borderRadius: 999,
        padding: '8px 10px',
      }}
    >
      {WEB_ICO[icon]('var(--cta-default)', 13)}
      <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}>{label}</span>
    </div>
  );
}

function GoalRing({ value, goal, label, unit }: { value: number; goal: number; label: string; unit: string }) {
  const pct = goal > 0 ? Math.min(100, Math.round((value / goal) * 100)) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <WebRing pct={pct} size={46} stroke={5} color={pct >= 100 ? 'var(--success)' : 'var(--cta-default)'}>
        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 700, color: 'var(--ink)' }}>
          {pct}%
        </span>
      </WebRing>
      <div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink)' }}>
          {value}/{goal} <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>{unit}</span>
        </div>
      </div>
    </div>
  );
}

interface WebPathScreenProps {
  narrow?: boolean;
  userName: string;
  gamification: GamificationView | null;
  goals: { vocab: number; grammar: number };
  onOpenUnit: (unit: UnitSummary, track?: 'VOCAB' | 'GRAMMAR' | 'PRACTICE') => void;
  onStartReview: () => void;
  onOpenGoal: () => void;
  onOpenMockTest: (test: MockTestSummary) => void;
  /** Đổi giá trị để fetch lại /me/progress (sau khi hoàn thành lesson/bộ đề) — xem WebAppShell. */
  reloadKey?: number;
}

export function WebPathScreen({ narrow = false, userName, gamification, goals, onOpenUnit, onStartReview, onOpenGoal, onOpenMockTest, reloadKey }: WebPathScreenProps) {
  const course = useAsync(() => contentApi.getCourse(DEFAULT_COURSE_ID), []);
  const progress = useAsync(() => progressApi.getProgress(), [reloadKey]);
  // Thẻ "Bài test" sau mỗi 5 Bài — lỗi tải thì Lộ trình vẫn hiện đủ, chỉ thiếu thẻ test.
  const mockTests = useAsync(() => mockTestApi.list(), [reloadKey]);

  const foundationUnits = useMemo(
    () => (course.data ? course.data.units.filter((u) => u.order < BAI_PATH_ORDER_OFFSET + 1) : []),
    [course.data],
  );
  const branchUnits = useMemo(
    () => (course.data ? course.data.units.filter((u) => u.order >= BAI_PATH_ORDER_OFFSET + 1) : []),
    [course.data],
  );
  const branchList = useMemo(() => branchUnits.map((u) => branchNodes(u, progress.data)), [branchUnits, progress.data]);

  const rail = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: narrow ? '100%' : 320, flexShrink: 0 }}>
      {/* SRS tắt thì không có hàng đợi ôn để mời vào — xem công tắc ở Cài đặt SRS. */}
      {(gamification?.srsEnabled ?? true) && (
        <button
          onClick={onStartReview}
          style={{
            textAlign: 'left',
            background: 'rgba(179,205,224,0.2)',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--radius-card)',
            padding: 20,
            cursor: 'pointer',
            fontFamily: 'var(--font-vi)',
          }}
        >
          <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--navy-300)', marginBottom: 10 }}>Hôm nay</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: 40, fontWeight: 700, color: 'var(--ink)', lineHeight: 1 }}>{gamification?.srsDueCount ?? 0}</span>
            <span style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-secondary)' }}>thẻ đến hạn</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 6 }}>{`· tối đa ${gamification?.srsSessionSize ?? 20} thẻ mỗi lần ôn`}</div>
          <span
            style={{
              display: 'block',
              marginTop: 14,
              height: 48,
              lineHeight: '48px',
              textAlign: 'center',
              borderRadius: 'var(--radius-control)',
              background: 'var(--cta-default)',
              color: '#fff',
              fontWeight: 700,
              fontSize: 15,
            }}
          >
            Bắt đầu ôn
          </span>
        </button>
      )}

      <button
        onClick={onOpenGoal}
        style={{
          textAlign: 'left',
          background: 'var(--surface-page)',
          border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-card)',
          padding: 20,
          cursor: 'pointer',
          fontFamily: 'var(--font-vi)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--ink)' }}>Mục tiêu hôm nay</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 2, fontSize: 12.5, fontWeight: 600, color: 'var(--cta-default)' }}>
            Chỉnh {WEB_ICO.chev('var(--cta-default)', 14)}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <GoalRing value={gamification?.learnedToday.vocab ?? 0} goal={goals.vocab} label="Từ vựng" unit="từ" />
          <GoalRing value={gamification?.learnedToday.grammar ?? 0} goal={goals.grammar} label="Ngữ pháp" unit="mẫu" />
        </div>
      </button>
    </div>
  );

  const nodeSize = narrow ? 64 : 78;

  const path = course.loading ? (
    <LoadingState />
  ) : course.error || !course.data ? (
    <ErrorState message="Không tải được lộ trình học. Kiểm tra API đã chạy chưa." />
  ) : (
    <div style={{ flex: 1, minWidth: 0, display: 'flex', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 560 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: 18, gap: 12 }}>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--ink)', margin: 0 }}>{`Chào bạn, ${userName}!`}</h1>
          <StreakXpBadge streak={gamification?.streak ?? 0} />
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center', marginBottom: 18 }}>
          Lộ trình N5
        </div>

        {foundationUnits.map((unit, i) => (
          <div key={unit.id}>
            {i > 0 && <PathConnector done={(progress.data?.foundation[foundationUnits[i - 1].order]?.state ?? 'open') === 'done'} height={34} />}
            <PathNode unit={foundationNode(unit, progress.data)} size={nodeSize} onOpen={() => onOpenUnit(unit)} />
          </div>
        ))}

        {branchList.length > 0 && (
          <>
            <PathConnector done={foundationUnits.every((u) => (progress.data?.foundation[u.order]?.state ?? 'open') === 'done')} height={34} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: BRANCH_GAP, margin: '6px 0 14px' }}>
              <TrackHeadPill icon="heart" label="Từ vựng" />
              <TrackHeadPill icon="book" label="Ngữ pháp" />
              <TrackHeadPill icon="edit" label="Bài tập" />
            </div>
            {branchList.map((row, i) => {
              // Hàng ngay sau thẻ Bài test đã có ngoặc toả xuống từng cột — bỏ đường nối thẳng.
              const prev = branchList[i - 1];
              const afterTest = prev != null && (mockTests.data ?? []).some((t) => t.toBai === prev.baiNumber);
              const connect = i > 0 && !afterTest;
              const test = (mockTests.data ?? []).find((t) => t.toBai === row.baiNumber);
              const rowDone = row.vocab.state === 'done' && row.grammar.state === 'done' && row.exercise.state === 'done';
              return (
                <Fragment key={row.vocab.id}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: BRANCH_GAP }}>
                    <div>
                      {connect && <PathConnector done={prev.vocab.state === 'done'} height={34} />}
                      <PathNode unit={row.vocab} size={nodeSize} onOpen={() => onOpenUnit(branchUnits[i], 'VOCAB')} />
                    </div>
                    <div>
                      {connect && <PathConnector done={prev.grammar.state === 'done'} height={34} />}
                      <PathNode unit={row.grammar} size={nodeSize} onOpen={() => onOpenUnit(branchUnits[i], 'GRAMMAR')} />
                    </div>
                    <div>
                      {connect && <PathConnector done={prev.exercise.state === 'done'} height={34} />}
                      <PathNode unit={row.exercise} size={nodeSize} onOpen={() => onOpenUnit(branchUnits[i], 'PRACTICE')} />
                    </div>
                  </div>
                  {test && (
                    <>
                      <TestBracket kind="in" done={rowDone} gap={BRANCH_GAP} />
                      <MockTestCard test={test} onOpen={() => onOpenMockTest(test)} />
                      {i < branchList.length - 1 && <TestBracket kind="out" done={test.bestPassed} gap={BRANCH_GAP} />}
                    </>
                  )}
                </Fragment>
              );
            })}
          </>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: narrow ? 'column' : 'row-reverse', alignItems: 'flex-start', gap: 24, fontFamily: 'var(--font-vi)' }}>
      {narrow ? rail : <div style={{ position: 'sticky', top: 0, width: 320, flexShrink: 0 }}>{rail}</div>}
      {path}
    </div>
  );
}
